angular.module('rjmNg.services').factory 'figureService', [
  "dateService"
  "chartFormatterService"
  "operatorEnums"
  "figureEnums"
  "timeRangeEnums"
  "metricDataTypes"
  "utils"
  "reportQueryType"
  "app"
  "rjqlStringConstants"
  "colorThemeService"
  (
    dateService
    chartFormatterService
    operatorEnums
    figureEnums
    timeRangeEnums
    metricDataTypes
    utils
    reportQueryType
    app
    rjqlStringConstants
    colorThemeService
  ) ->

    DATA_TYPES = metricDataTypes.DATA_TYPES
    TIME_INTERVAL_VALUES = timeRangeEnums.TIME_INTERVAL_VALUES

    # Calculate the number of groupings
    getNumberOfGroupings = (figure) ->
      _.size(figure.data.headers.rows)

    # Calculate the number of metrics
    getNumberOfMetrics = (figure) ->
      _.size(figure.data.headers.measures)

    getMetricNameForToolTip = (figure, metricIndex) ->
      figure.data.metricHeaders[metricIndex]

    getDateTimeDataForToolTip = (figure, dateTimeIndex) ->
      figure.data.columns[dateTimeIndex]

    _getNumberOfMetricsHeaders = (figure) ->
      _.size(figure.data.metricHeaders)

    # Determines whether a given figure is stacked.
    figurePresentationIsStacked = (figure) ->
      plotOptions = figure.presentation.highcharts?.plotOptions

      return plotOptions?.column?.stacking? or
        plotOptions?.area?.stacking? or
        plotOptions?.bar?.stacking? or
        plotOptions?.series?.stacking?

    doesFigureHaveMultiYAxes = (figure) ->
      if figure.presentation.multiYAxes?
        return figure.presentation.multiYAxes
      else
        return figure.presentation.highcharts.chart?.yAxis? and
          figure.presentation.highcharts.chart?.yAxis isnt null

    doesFigureHaveTrendLine = (figure) ->
      return figure.presentation.hasTrendLine is true

    doesFigureHaveEnabledLegend = (figure) ->
      return not figure.presentation.highcharts.chart.legend?.enabled? or
        figure.presentation.highcharts.chart.legend.enabled

    # For a given percentage value, return the value for the yAxis.max property
    _getYMaxValue = (val) ->
      if val >= figureEnums.PERCENT_Y_AXIS_SCALING.LOWER_THRESHOLD and
        val <= figureEnums.PERCENT_Y_AXIS_SCALING.UPPER_THRESHOLD
          return 1
      else
        return null

    _getMinMaxForHighchart = (figure, dataset) ->
      all = []
      allNegative = []
      if figurePresentationIsStacked figure
        for row in figure.data.rows # Loop through each series
          for sourceData in row.data
            for datum, key in sourceData # Loop through each data point and hold on to the key
              # Build an array of positive values
              all[key] ?= 0
              all[key] += datum if datum > 0

              # Build an array of negative values
              allNegative[key] ?= 0
              allNegative[key] += datum if datum < 0
      else
        # The unstacked charts are much easier
        for row in figure.data.rows
          for sourceData in row.data
            # concatenates all datapoints in one array
            all = all.concat sourceData

            # We can even use the same list, since we're looking for the overall largest and most negative
            allNegative = all

      range =
          min: Math.min.apply null, allNegative
          max: Math.max.apply null, all

      return range

    # Gets the median number in an array
    _getDatasetMedian = (dataset) ->
      return null if dataset.length is 0

      nonEmptyData = []
      for datum in dataset when datum isnt 0 and datum isnt null
        nonEmptyData.push datum

      if nonEmptyData.length is 0 then return 0

      sorted = nonEmptyData.slice().sort((a, b) -> a - b)
      if sorted.length % 2 is 1 # odd number of elements, middle element is median
        sorted[(sorted.length - 1) / 2]
      else # even number of elements, take average of middle two elements
        (sorted[(sorted.length / 2) - 1] + sorted[(sorted.length / 2)]) / 2

    # Gets information used to format display values when all metrics share a single y-axis
    _getMeasureDataFromFigureDataForOneAxis = (figure) ->
      sharedPrefix = ""
      sharedSuffix = ""
      sharedDataType = DATA_TYPES.nonMonetary
      median = null

      singlePrefix = null
      singleSuffix = null
      singleDataType = null

      canFormatSingleAxis = true

      dataset = []
      for row in figure.data.rows
        for data in row.data
          dataset = dataset.concat(data)

      for measure, measureIndex in figure.data.headers.measures
        if measure.suffix is '%' then measure.dataType = DATA_TYPES.percentage

        if measureIndex is 0
          singlePrefix = measure.prefix
          singleSuffix = measure.suffix
          singleDataType = measure.dataType
          continue

        if singlePrefix isnt measure.prefix or
        singleSuffix isnt measure.suffix or
        singleDataType isnt measure.dataType
          canFormatSingleAxis = false
          break

      if canFormatSingleAxis
        sharedPrefix = singlePrefix
        sharedSuffix = singleSuffix
        sharedDataType = singleDataType

      if sharedDataType is DATA_TYPES.seconds or sharedDataType is DATA_TYPES.bytes
        median = _getDatasetMedian dataset

      return [sharedPrefix, sharedSuffix, sharedDataType, median]

    _getDataTypeFromRJQLResponse = (rjqlResponse, metricIndex) ->
      dataType = rjqlResponse.headers.measures?[metricIndex]?.dataType
      dataType ?= ''
      return dataType

    _getPrefixFromRJQLResponse = (rjqlResponse, metricIndex) ->
      prefix = rjqlResponse.headers.measures?[metricIndex]?.prefix
      prefix ?= ''
      return prefix

    _getSuffixFromRJQLResponse = (rjqlResponse, metricIndex) ->
      suffix = rjqlResponse.headers.measures?[metricIndex]?.suffix
      suffix ?= ''
      return suffix

    # Gets information used to format display values from the rjql response
    getMeasureDataFromFigureData = (rjqlResponse, dataset, metricIndex, calcRange = true) ->
      prefix = _getPrefixFromRJQLResponse(rjqlResponse, metricIndex) ? ''
      suffix = _getSuffixFromRJQLResponse(rjqlResponse, metricIndex) ? ''
      dataType = _getDataTypeFromRJQLResponse(rjqlResponse, metricIndex) ? DATA_TYPES.nonMonetary
      range = null
      median = null

      # only these data types need the range to calculate display precision
      if calcRange and (dataType is DATA_TYPES.currency or dataType is DATA_TYPES.nonMonetary)
        range =
          min: Math.min dataset...
          max: Math.max dataset...

      # only these data types need the median to calculate display suffix
      if dataType is DATA_TYPES.seconds or dataType is DATA_TYPES.bytes
        median = _getDatasetMedian dataset

      return [prefix, suffix, dataType, range, median]

    _getMeasureData = (figure) ->
      measureData = []
      for row in figure.data.rows
        for dataset, datasetIndex in row.data
          measureData.push getMeasureDataFromFigureData(figure.data, dataset, datasetIndex, false)
      return measureData
    
    _setAxisIdx = (axisType) ->
      { "xAxis": 0, "yAxis": 1, "zAxis": 2}[axisType]
      
    # Options added to yAxis and xAxis(for scatterPlot) when rendering
    genAxisOptions = (figure, highchart, axisType) ->
      if not highchart?.yAxis? # Default y-axis / figure doesn't contain multi axis presentation data
        range = _getMinMaxForHighchart(figure)
        [prefix, suffix, dataType, median] = _getMeasureDataFromFigureDataForOneAxis(figure)
        yAxis =
          min: Math.min(range.min, 0)
          labels:
            useHTML: true
            formatter: ->
              axisRange =
                max: this.axis.max
                min: this.axis.min
              median = Math.floor((axisRange.max - axisRange.min)/2)
              chartFormatterService.getFormattedValue(this.value, prefix, suffix, dataType, axisRange, median)
          plotLines: [{
                value: 0
                width: 1
                color: '#aaaaaa'
            }]

        if not yAxis.max and dataType is DATA_TYPES.percentage
          yAxis.max = _getYMaxValue(range.max)
        return yAxis

      yAxes = if getChartTypeFromFigure(figure) in ['scatter', 'scattertrendline', 'bubble']
                 highchart[axisType]
              else
                highchart.yAxis # The yAxis property is really an array of axes

      measureData = []
      for row in figure.data.rows
        for dataset, datasetIndex in row.data
          measureData.push(getMeasureDataFromFigureData(figure.data, dataset, datasetIndex, false))

      for axis, axisIndex in yAxes
        # Set axis minimum to smallest value of all series data on this axis
        minValue = (Math.min series.data... for series in highchart.series when series[axisType] is axisIndex)
        axis.min = Math.min(Math.min(minValue...), 0)

        if not axis.max and measureData[axisIndex][2] is DATA_TYPES.percentage
          maxValue = (Math.max series.data... for series in highchart.series when series[axisType] is axisIndex)
          axis.max = _getYMaxValue(maxValue)

        axis.useHTML = true
        axis.labels.formatter = ->
          axisRange =
            max: this.axis.max
            min: this.axis.min

          if getChartTypeFromFigure(figure) in ['scatter', 'scattertrendline', 'bubble']
            idx = _setAxisIdx(axisType)
            [prefix, suffix, dataType, range] = measureData[idx]
          else
            index = this.axis.userOptions.index
            [prefix, suffix, dataType, range] = measureData[index]
            
          median = Math.floor((axisRange.max - axisRange.min)/2)
          chartFormatterService.getFormattedValue(this.value, prefix, suffix, dataType, axisRange, median)
        
      return yAxes

    # Get the yaxis title for this measure from the figure
    _getAxesTitle = (figure, measureIndex) ->
      # Multiple metrics
      measures = getMeasuresFromFigure(figure)
      if measures.length > 1
        if measures[measureIndex] instanceof Array # Regular metric
          return measures[measureIndex][0]
        else return measures[measureIndex].label # Formula metric
      # Single metric
      else
        if measures[0].label # Fromula metric
          return measures[0].label
        else if measures[0] instanceof Array #Regular metric with hidden metrics
          return measures[0][0]
        else #Regular metric
          return figure?.query?.from?.alias

    # Creates the axes object that's part of the highcharts object
    # isMultiAxes is a paramater being passed that's defined as a chart object having multiple axes of the same axis
    _genAxesObject = (figure, measureIndex, isMultiAxes = true) ->
      title = _getAxesTitle(figure, measureIndex)

      if app.hasFeatureFlag('UNIQUE_SERIES_COLORS')
        color = chartFormatterService.getColorByString(title)
      else
        color = colorThemeService.pickColor(measureIndex)

      axis =
        title:
          text: title
          style:
            color: color
        labels:
          style:
            color: if isMultiAxes then color else false
        #Put odd-indexed axes on the right, even-indexed ones on the left
        opposite: if isMultiAxes then measureIndex % 2 is 1 else false
      return axis

    # Does formatting for axi(e)s
    formatAxes = (figure, isMultiAxes) ->
      axes = []
      # get the measures from data.headers or query.select
      if(figure.data?.headers?)
        measures = figure.data.headers.measures
      else
        measures = getMeasuresFromFigure(figure)

      for measure, measureIndex in measures
        axes.push _genAxesObject(figure, measureIndex, isMultiAxes)
      return axes

    # Returns an array of measures from the figure
    getMeasuresFromFigure = (figure) ->
      if figure.query?.select?.measures?
        measures = (measure for measure in figure.query.select.measures)
      else
        measures = []
      return measures

    _hasSingleFormulaMeasure = (figure) ->
      measures = getMeasuresFromFigure(figure)
      return measures.length is 1 and measures[0].type is 'formula'

    # Tests to see if this is a single source query or not
    isSingleSourceFigure = (figure) ->
      from = figure.query.from
      measures = getMeasuresFromFigure(figure)
      measureCount = measures.length
      measureCount ?= 0
      multiFrom = from instanceof Array and from.length > 1
      return not (multiFrom or measureCount > 1 or _hasSingleFormulaMeasure(figure))

    _isStackedType = (type) ->
      type in ['columnstacked', 'barstacked', 'area']

    _getStackedType = (type) ->
      typeMap =
        column: 'columnstacked'
        bar: 'barstacked'
        area: 'area'
      return if typeMap[type]? then typeMap[type] else type

    _getUnstackedType = (type) ->
      typeMap =
        columnstacked: 'column'
        barstacked: 'bar'
        area: 'area'
      return if typeMap[type]? then typeMap[type] else type
  
    # Returns the highchart type from a figure
    getChartTypeFromFigure = (figure, getRjType = false) ->
      type = 'line'
      if figure.presentation?.highcharts.chart?
        type = figure.presentation.highcharts.chart.type
        if figurePresentationIsStacked(figure) and getRjType
          type = _getStackedType(type)
        if type is 'scatter' and doesFigureHaveTrendLine(figure) is true
          type = 'scattertrendline'
      return type

    _setChartTypeWithStacking = (figure, type, stacking) ->
      figure.presentation.highcharts.chart.type = _getUnstackedType(type)
      figure.presentation.highcharts.plotOptions =
        series:
          stacking: stacking
      return figure

    setChartType = (figure, type) ->
      if figure.presentation.highcharts.chart?
        stacking = if _isStackedType(type) then 'normal' else null
        figure = _setChartTypeWithStacking(figure, _getUnstackedType(type), stacking)
      else
        figure.presentation.highcharts.chart =
          type: type

      return figure

    _canRenderRjql = (figure) ->
      figure.data? and
        figure.query and
        figure.presentation.highcharts? and
        figure.reportQueryType is reportQueryType.RJQL

    getTimeInterval = (figure) ->
      columns = figure.query.select.columns

      unless columns?
        return TIME_INTERVAL_VALUES.NONE

      # Set the correct category label for cohort charts
      if isCohortFigure(figure)
        return columns[0].label
      else if columns?[0]?.time?.indexOf?("time") isnt -1
        return columns[0]?.interval
      else
        return TIME_INTERVAL_VALUES.NONE

    # Is this figure grouped by dimensions?
    isGroupedByDimension = (figure) ->
      figure.data.headers?.rows.length > 0 or
        typeof figure.data.headers?.rows?[0] is 'string'

    getUnDimensionedMeasureNames = (figure) ->
      names = []
      from = figure.query.from

      for measure in getMeasuresFromFigure(figure)
        if measure.label? # Calculated metrics have labels
          names.push measure.label
        else if measure is ":value"
          if from instanceof Array
            for currentFrom in from
              names.push currentFrom.alias
          else
            names.push from.alias
        else
          names.push measure[0]
      return names

    correctNullOrBlankValue = (name) ->
      if name is null
        "[NULL]"
      else if name is ""
        "[blank]"
      else if Array.isArray(name)
        name.map(correctNullOrBlankValue)
      else
        name

    _getColumnNames = (figure) ->
      columnNames = []

      if isGroupedByDimension(figure)
        # Use series names for column names
        columnNames = (row.name for row in figure.data.rows)
      else
        # Use metric names for series names
        if figure.query.from instanceof Array
          columnNames = columnNames.concat getUnDimensionedMeasureNames(figure)
        else
          # Handles the case when the FROM statement is an object instead of a single value array
          columnNames.push figure.query.from.alias

      return columnNames.map(correctNullOrBlankValue)

    # Parse out the category names
    _getCategoriesFromFigure = (figure) ->
      categories = []
      timeInterval = getTimeInterval(figure)
      if timeInterval isnt TIME_INTERVAL_VALUES.NONE
        # We get single length array of arrays in data.columns when we have multiple values
        for column in figure.data.columns
          if figure.data.columns.length > 1
            categories.push column[0]
          else
            # Here we just have an array, so pass the whole column in
            categories.push column

        if figure.data.columns.length > 1 or
          (figure.data.columns.length is 1 and
          figure.data.columns[0] is "Value")
            categories = categories?.map (date) ->
              dateService.shortFormat(date, timeInterval, true)
        else
          categories = categories[0]?.map (date) ->
            dateService.shortFormat(date, timeInterval, true)
      else
        if isGroupedByDimension(figure)
          categories = _getColumnNames(figure)
        else
          categories = ['Total']

      return categories

    _getPieOrFunnelSeriesDataFromFigure = (figure) ->
      isGrouped = isGroupedByDimension(figure)
      type = getChartTypeFromFigure(figure)
      dataLabels = []
      if isGrouped
        for row in figure.data.rows
          row.name ?= 'null'
          dataLabels.push([row.name.toString(), row.data[0][0]])
      else # isn't grouped, use time interval for series
        timeInterval = getTimeInterval(figure)
        # loop over rows or columns (whichever length is shorter)
        for i in [0..Math.min(figure.data.columns.length - 1, figure.data.rows[0].data[0].length - 1)]
          if timeInterval is TIME_INTERVAL_VALUES.NONE
            # with no time interval the i-th column is a string containting the series name
            dataLabels.push([figure.data.columns[i].toString(), figure.data.rows[0].data[0][i]])
          else # time interval isn't 'None'
            dataLabels.push([dateService.format(figure.data.columns[i][0].toString(), timeInterval, false), figure.data.rows[0].data[0][i]])
      series = [{
        type: type
        name: figure.query.from.alias
        data: dataLabels
      }]

      return series

    # Omit all null data points in scatter series
    _cleanDataForScatter = (array) ->
      array.filter((el) -> el.x? and el.y?)

    # Omit all null data points in bubble series
    _cleanDataForBubble = (array) ->
      array.filter((el) -> el.x? and el.y? and el.z?)

    _getMultiSeriesDataForBubbleChart = (figure) ->
       _cleanDataForBubble(figure.data.rows.map((row) -> {
        name: row.name[0],
        groupByName: row.name[1],
        x: row.data[0][0],
        y: row.data[1][0],
        z: row.data[2][0]
      }))

    _groupMultiSeriesData = (figure) ->
      data = _getMultiSeriesDataForBubbleChart(figure)
      _.groupBy(data, (row) -> row.groupByName)

    _bubbleChartMultiSeries = (figure) ->
      series = []
      data = _groupMultiSeriesData(figure)

      for key, value of data
        series.push({
          type: 'bubble'
          name: key
          data: value
          multiSeries: true
          lineWidth: 0
        })
      return _.sortBy(series, 'name')

    _getBubbleChartDataLabels = (figure) ->
      _cleanDataForBubble(figure.data.rows.map((row) -> {name: row.name, x: row.data[0][0], y: row.data[1][0], z: row.data[2][0]}))

    _getBubbleChartDataLabelsByTimeInterval = (figure) ->
      names = figure.data.columns.map((column)-> column[0])
      xyzs = _.zip(names, figure.data.rows[0].data[0], figure.data.rows[0].data[1], figure.data.rows[0].data[2])
      return _cleanDataForBubble(xyzs.map((i) -> {name: i[0], x: i[1], y: i[2], z: i[3]}))

    _getScatterChartDataLabels = (figure) ->
      _cleanDataForScatter(figure.data.rows.map((row) -> {name: row.name, x: row.data[0][0], y: row.data[1][0]}))

    _getScatterDataLabelsByTimeInterval = (figure) ->
      names = figure.data.columns.map((column)-> column[0])
      xyzs = _.zip(names, figure.data.rows[0].data[0], figure.data.rows[0].data[1])
      return _cleanDataForScatter(xyzs.map((i) -> {name: i[0], x: i[1], y: i[2]}))

    _groupedByDataLabels = (figure) ->
      if _getNumberOfMetricsHeaders(figure) is 3
        _getBubbleChartDataLabels(figure)
      else
        _getScatterChartDataLabels(figure)

    _timeIntervalDataLabels = (figure) ->
      if _getNumberOfMetricsHeaders(figure) is 3
        _getBubbleChartDataLabelsByTimeInterval(figure)
      else
        _getScatterDataLabelsByTimeInterval(figure)

    _setSeriesTypeToScatterOrBubble = (figure) ->
      if _getNumberOfMetricsHeaders(figure) is 3 then 'bubble' else 'scatter'

    _getScatterAndBubbleSeriesDataFromFigure = (figure) ->
      seriesType = _setSeriesTypeToScatterOrBubble(figure)
      type = getChartTypeFromFigure(figure)
      dataLabels = []
      if figure.data.columns[0] is 'Value'
        dataLabels = _groupedByDataLabels(figure)
      else
        dataLabels = _timeIntervalDataLabels(figure)

      sortedLabels = _.sortBy(dataLabels, 'x')

      if getNumberOfGroupings(figure) is 2
        series = _bubbleChartMultiSeries(figure)
      else
        series = [{
          type: seriesType
          name: figure.query.from.alias
          data: sortedLabels
          multiSeries: false
          lineWidth: 0
        }]
      if type is 'scattertrendline'
        series.push(_getScatterTrendLineFromSeriesData(sortedLabels))
      return series

    _getScatterTrendLineFromSeriesData = (seriesData) ->
      # Calculate mathematical formula for trendline
      xValues = []
      sumXValues = 0
      sumYValues = 0
      numberDataPoints = 0
      sumOfProductsOfXTimesY = 0
      xValuesSquared = 0
      for row in seriesData
        numberDataPoints += 1
        xValues.push(row.x)
        sumXValues += row.x
        sumYValues += row.y
        sumOfProductsOfXTimesY += (row.x * row.y)
        xValuesSquared += (row.x * row.x)

      slope = ((numberDataPoints * sumOfProductsOfXTimesY) - (sumXValues * sumYValues)) / ((numberDataPoints * xValuesSquared) - (sumXValues * sumXValues))
      intercept = (sumYValues - (slope * sumXValues)) / numberDataPoints
      minXValue = _.min(xValues)
      maxXValue = _.max(xValues)
      trendPoints = []
      trendPoints.push([minXValue, (slope * minXValue + intercept)])
      trendPoints.push([maxXValue, (slope * maxXValue + intercept)])
      series = {
        type: 'line'
        name: 'Average'
        data: trendPoints
      }
      return series

    isPieFunnelAllowed = (figure) ->
      series = getSeriesDataFromFigure(figure)
      return series.length < 2 or series[0].data.length is 1

    _isScatterAllowed = (figure) ->
      twoMetrics = getNumberOfMetrics(figure) is 2
      numOfGroupings = getNumberOfGroupings(figure)
      timeIntervalSetToNone = getTimeInterval(figure) is TIME_INTERVAL_VALUES.NONE
      return twoMetrics and
             ((numOfGroupings is 1 and timeIntervalSetToNone) or
              (numOfGroupings is 0 and not timeIntervalSetToNone))

    _isSingleSeriesBubbleChart = (figure) ->
      threeMetrics = getNumberOfMetrics(figure) is 3
      numOfGroupings = getNumberOfGroupings(figure)
      timeIntervalSetToNone = getTimeInterval(figure) is TIME_INTERVAL_VALUES.NONE
      return threeMetrics and
             ((numOfGroupings is 1 and timeIntervalSetToNone) or
              (numOfGroupings is 0 and not timeIntervalSetToNone))

    _isMultiSeriesBubbleChart = (figure) ->
      threeMetrics = getNumberOfMetrics(figure) is 3
      twoGroupings = getNumberOfGroupings(figure) is 2
      timeIntervalSetToNone = getTimeInterval(figure) is TIME_INTERVAL_VALUES.NONE
      return threeMetrics and twoGroupings and timeIntervalSetToNone

    isScatterBubbleAllowed = (figure) ->
      return _isScatterAllowed(figure) or
             _isSingleSeriesBubbleChart(figure) or
             _isMultiSeriesBubbleChart(figure)

    getGroupByHeatMapCategoryNames = (figure, axis) ->
      names = figure.data.rows.map((row) -> row.name[axis])
      uniqNames = _.uniq(names)
      return _.sortBy(uniqNames)

    getTimeIntervalHeatMapCategoryNames = (figure) ->
      names = figure.data.rows.map((row) -> row.name)
      return _.uniq(names)

    _getHeatMapDataWithTimeIntervalSet = (figure) ->
      dataLabels = []
      data = figure.data.rows
      colLength = figure.data.rows.length - 1

      for column, colIndex in data
        for row, rowIndex in column.data[0]
          dataLabels.push({
            x: rowIndex
            y: colLength - colIndex
            value: row
          })
      return dataLabels

    _getHeatMapDataWithTwoGroupBy = (figure) ->
      xAxisCategories = getGroupByHeatMapCategoryNames(figure, 0) # ['Ready','Done','In Progress']
      yAxisCategories = getGroupByHeatMapCategoryNames(figure, 1) # ['Cash','Credit','Debit']
      colLength = xAxisCategories.length - 1

      # The following two lines of code are done to ensure
      # that all blank heatmap cell values have null assigned to them
      nullVals = yAxisCategories.map((i) -> null) # [null,null,null]
      nullMatrix = xAxisCategories.map((i) -> angular.copy(nullVals)) #[[null,null,null], [null,null,null], [null,null,null]]

      dataLabels = []

      for row, rowIndex in figure.data.rows
        yIdx = _.indexOf(yAxisCategories, row.name[1])
        xIdx = _.indexOf(xAxisCategories, row.name[0])
        # Replaces the null value on the nullMatrix with a data point value
        nullMatrix[xIdx][yIdx] = row.data[0][0]

      for column, colIndex in nullMatrix
        for row, rowIndex in column
          dataLabels.push({
            x: rowIndex
            y: colLength - colIndex
            value: row
          })
      return dataLabels

    _getHeatMapData = (figure) ->
      if getNumberOfGroupings(figure) is 2
        return  _getHeatMapDataWithTwoGroupBy(figure)
      else
        return _getHeatMapDataWithTimeIntervalSet(figure)

    _getHeatMapSeriesDataFromFigure = (figure) ->
      series = [{
        type: 'heatmap'
        name: figure.query.from.alias
        data: _getHeatMapData(figure)
      }]
      return series

    _isGroupedByHeatMapType = (figure) ->
      return  getNumberOfMetrics(figure) is 1 and
              getNumberOfGroupings(figure) is 2 and
              getTimeInterval(figure) is TIME_INTERVAL_VALUES.NONE

    _isTimeIntervalHeatMapType = (figure) ->
      return  getNumberOfMetrics(figure) is 1 and
              getNumberOfGroupings(figure) is 1 and
              getTimeInterval(figure) isnt TIME_INTERVAL_VALUES.NONE

    isHeatMapAllowed = (figure) ->
      return _isGroupedByHeatMapType(figure) or _isTimeIntervalHeatMapType(figure)

    _getDataLabelsFromStandardForFunnelPie = (figure) ->
      dataLabels = []
      for i in [0..figure.data.rows[0].data.length-1]
        dataLabels.push([figure.data.metricHeaders[i].toString(), figure.data.rows[0].data[i][0]])
      return dataLabels

    _getDataLabelsFromGroupByForFunnelPie = (figure) ->
      dataLabels = []
      for i in [0..figure.data.rows.length-1]
        dataLabels.push([figure.data.rows[i].name, figure.data.rows[i].data[0][0]])
      return dataLabels

    _getFunnelPieMultipleMetricsSeriesDataFromFigure = (figure) ->
      if figure.data.rows.length > 1
        dataLabels = _getDataLabelsFromGroupByForFunnelPie(figure)
      else
        dataLabels = _getDataLabelsFromStandardForFunnelPie(figure)

      series = [{
        type: getChartTypeFromFigure(figure)
        data: dataLabels
      }]

      return series

    # Highcharts hates empty strings and needs null values
    _convertEmptyStringsToNull = (dataPoints) ->
      if dataPoints instanceof Array
        return (dataPoints.map (datum) -> if datum is "" then null else datum)
      else
        return if dataPoints is "" then null else dataPoints

    _getSeriesDataNoTimeInterval = (figure, useDataIndexForYAxis, isGrouped) ->
      series = []
      for row, index in figure.data.rows
        if row?
          isSingleSource = isSingleSourceFigure(figure)
          for dataPoints, dataIndex in row.data
            name = " "
            if not isSingleSource
              name = "#{figure.query.select.measures[dataIndex][0] ? figure.query.select.measures[dataIndex].label}"

            if series.length <= dataIndex or not isGrouped
              series.push
                name: name
                data: _convertEmptyStringsToNull(dataPoints)
                yAxis: if useDataIndexForYAxis then dataIndex else 0
            else
              series[dataIndex].data = series[dataIndex].data.concat(_convertEmptyStringsToNull(dataPoints))

      return series

    _getSeriesDataWithTimeInterval = (figure, useDataIndexForYAxis, isGrouped) ->
      series = []
      columnNames = _getColumnNames(figure)
      timeInterval = getTimeInterval(figure)
      for row, index in figure.data.rows
          if row?
            isSingleSource = isSingleSourceFigure(figure)
            measures = getMeasuresFromFigure(figure)
            oneVisibleMetric = measures.length is 1
            for dataPoints, dataIndex in row.data
              dimensionName = columnNames[if isGrouped then index else dataIndex]
              if dimensionName instanceof Array then dimensionName = dimensionName.join(', ')
              if dimensionName is null
                name = null
              else if isCohortFigure(figure)
                name = row.name
              else if isSingleSource or not isGrouped
                name = "#{dimensionName}"
              else if measures[dataIndex] instanceof Array # regular metrics
                metricName = measures[dataIndex]?[0]
                if oneVisibleMetric then name = "#{dimensionName}"
                else name = "#{metricName}, #{dimensionName}"
              else if measures[dataIndex]?.label? # Formula metrics
                metricName = measures[dataIndex].label
                if oneVisibleMetric then name = "#{dimensionName}"
                else name = "#{metricName}, #{dimensionName}"

              seriesData =
                name: name
                data: _convertEmptyStringsToNull(dataPoints)
                yAxis: if useDataIndexForYAxis then dataIndex else 0

              # Change chart line type when perspective is rolling average
              if measures[dataIndex]?.op is 'average'
                  _.extend(seriesData, {type: 'spline'})

              series.push(seriesData)
      return series

    getSeriesDataFromFigure = (figure) ->
      isGrouped = isGroupedByDimension(figure)

      useDataIndexForYAxis = doesFigureHaveMultiYAxes(figure)

      timeInterval = getTimeInterval(figure)

      if timeInterval is TIME_INTERVAL_VALUES.NONE
        return _getSeriesDataNoTimeInterval(figure, useDataIndexForYAxis, isGrouped)
      else
        return _getSeriesDataWithTimeInterval(figure, useDataIndexForYAxis, isGrouped)

    _getSqlFigureColumnIndexes = (figure, names) ->
      indexes = []
      for name in names when name in figure.data.header
        indexes.push(figure.data.header.indexOf(name))
      return indexes

    _getSqlFigureLabelValues = (figure) ->
      columnNames = figure.presentation.highcharts.rj.labels
      columnIndexes = _getSqlFigureColumnIndexes(figure, columnNames)
      keys = []
      for row in figure.data.body
        key = ''
        for index in columnIndexes
          key += "#{row[index]} "
        keys.push(key.trim())
      return _.uniq(keys)

    _rjHighcharts = (figure) ->
      unless figure.presentation.highcharts.rj?
        figure.presentation.highcharts.rj =
          series: []
          categories: []
      figure.presentation.highcharts.rj.series ?= []
      figure.presentation.highcharts.rj.categories ?= []
      figure.presentation.highcharts.rj.labels ?= []
      return figure

    addSeries = (figure, name) ->
      figure = _rjHighcharts(figure)
      figure.presentation.highcharts.rj.series.push(name)
      return figure

    removeSeries = (figure, name) ->
      figure = _rjHighcharts(figure)
      index = figure.presentation.highcharts.rj.series.indexOf(name)
      if figure.presentation.highcharts.rj.series[index]?
        figure.presentation.highcharts.rj.series.splice(index, 1)
      return figure

    setSeries = (figure, names) ->
      figure = _rjHighcharts(figure)
      figure.presentation.highcharts.rj.series = names
      return figure

    _getSeries = (figure) ->
      figure = _rjHighcharts(figure)
      return figure.presentation.highcharts.rj.series

    addCategory = (figure, name) ->
      figure = _rjHighcharts(figure)
      figure.presentation.highcharts.rj.categories.push(name)
      return figure

    removeCategory = (figure, name) ->
      figure = _rjHighcharts(figure)
      index = figure.presentation.highcharts.rj.categories.indexOf(name)
      if figure.presentation.highcharts.rj.categories[index]?
        figure.presentation.highcharts.rj.categories.splice(index, 1)
      return figure

    _getCategories = (figure) ->
      figure = _rjHighcharts(figure)
      return figure.presentation.highcharts.rj.categories

    # Temporary. We can remove this once xAxisName never appears in any figures
    # This data should now be stored in highcharts.rj.categories
    _fixCategory = (figure) ->
      xAxisName = figure.presentation.highcharts.rj.xAxisName
      if xAxisName?
        figure.presentation.highcharts.rj.categories.push(xAxisName)
        delete figure.presentation.highcharts.rj.xAxisName
      return figure

    setCategories = (figure, names) ->
      figure = _rjHighcharts(figure)
      figure = _fixCategory(figure)
      figure.presentation.highcharts.rj.categories = names
      return figure

    addLabel = (figure, name) ->
      figure = _rjHighcharts(figure)
      figure.presentation.highcharts.rj.labels.push(name)
      return figure

    removeLabel = (figure, name) ->
      figure = _rjHighcharts(figure)
      index = figure.presentation.highcharts.rj.labels.indexOf(name)
      if figure.presentation.highcharts.rj.labels[index]?
        figure.presentation.highcharts.rj.labels.splice(index, 1)
      return figure

    _getLabels = (figure, name) ->
      figure = _rjHighcharts(figure)
      return figure.presentation.highcharts.rj.labels

    _refreshSqlFigureCategoryValues = (figure) ->
      figure = _rjHighcharts(figure)
      if figure.presentation.highcharts.rj?
        type = getChartTypeFromFigure(figure)
        categories = _getSqlFigureLabelValues(figure)
        figure.presentation.highcharts.xAxis =
          categories: categories
          tickInterval: 1
          labels:
            step: chartFormatterService.getStep(type, categories, null, 1)
      return figure

    _fillInSeries = (points, key, values) ->
      position = values.indexOf(key)
      while points.length < position
        points.push null
      return points

    _extendSeries = (series, values) ->
      while series.data.length < values
        series.data.push(null)
      return series

    _fixDataPoint = (point) ->
      modifiedPoint = switch
        when point is "" then null
        when point is null then null
        when isNaN(point) then null
        else +point

    _getLabelKey = (row, xAxisIndexes) ->
      key = ''
      for index in xAxisIndexes
        key += "#{row[index]} " # The trailing space is required here
      return key.trim()

    _getCategorizedSeries = (data, seriesNames, columnSeriesIndexes, categoryNames, categoryIndexes, xAxisIndexes, xAxisValues) ->
      series = []
      maxValues = 0
      for row, rowIndex in data.body
        xAxisKey = _getLabelKey(row, xAxisIndexes)
        for columnSeriesIndex, seriesIndex in columnSeriesIndexes
          categoryKey = seriesNames[seriesIndex]
          for catIndex in categoryIndexes
            categoryKey += " #{row[catIndex]}"
          unless series[seriesIndex]?
            series[seriesIndex] = {}
          unless series[seriesIndex][categoryKey]?
            series[seriesIndex][categoryKey] =
              name: categoryKey
              data: []

          series[seriesIndex][categoryKey].data = _fillInSeries(series[seriesIndex][categoryKey].data, xAxisKey, xAxisValues)

          series[seriesIndex][categoryKey].data.push _fixDataPoint(row[columnSeriesIndex])
          if maxValues < series[seriesIndex][categoryKey].data.length
            maxValues = series[seriesIndex][categoryKey].data.length

      finalSeries = []
      for row in series
        for key in Object.keys(row)
          finalSeries.push(_extendSeries(row[key], maxValues))

      return finalSeries

    _getUncategorizedSeries = (data, seriesNames, columnSeriesIndexes, xAxisIndexes, isPieOrFunnelChart) ->
      series = []
      for row, bodyIndex in data.body
        for columnSeriesIndex, seriesIndex in columnSeriesIndexes
          unless series[seriesIndex]?
            series[seriesIndex] =
              name: "#{seriesNames[seriesIndex]}"
              data: []

          indexData = _fixDataPoint(row[columnSeriesIndex])
          indexName = row[xAxisIndexes[seriesIndex]]
          if isPieOrFunnelChart
            series[seriesIndex].data.push { name: indexName, y: indexData }
          else
            series[seriesIndex].data.push indexData
      return series

    _convertSqlFigureDataToHighchartSeries = (figure) ->
      data = figure.data
      seriesNames = _getSeries(figure)
      categoryNames = _getCategories(figure)
      xAxisNames = _getLabels(figure)
      xAxisValues = _getSqlFigureLabelValues(figure)

      if seriesNames.length is 0
        seriesNames = data.header

      columnSeriesIndexes = _getSqlFigureColumnIndexes(figure, seriesNames)
      categoryIndexes = _getSqlFigureColumnIndexes(figure, categoryNames)
      xAxisIndexes = _getSqlFigureColumnIndexes(figure, xAxisNames)
      series = []

      isPieOrFunnelChart = getChartTypeFromFigure(figure) in ['pie', 'funnel']
      if categoryIndexes.length > 0
        series = _getCategorizedSeries(data, seriesNames, columnSeriesIndexes, categoryNames, categoryIndexes, xAxisIndexes, xAxisValues)
      else
        series = _getUncategorizedSeries(data, seriesNames, columnSeriesIndexes, xAxisIndexes, isPieOrFunnelChart)

      sortByName = (a, b) ->
        if a.name > b.name then return 1
        if a.name < b.name then return -1
        return 0

      return series.sort(sortByName)

    # Accepts a full figure object and gives you back a highcharts object
    getChartFromFigure = (figure) ->
      if _canRenderRjql(figure)
        # Initliaze with presentation data
        chartObject = utils.copy(figure.presentation.highcharts)

        # Set x-axis categories if scatter chart x-axis data has strings
        if getChartTypeFromFigure(figure) in ['scatter', 'scattertrendline']
          chartObject.xAxis = figure.presentation.highcharts.chart.xAxis
        else if getChartTypeFromFigure(figure) is 'heatmap' and getNumberOfGroupings(figure) is 2
          # Heat Map based on Group By
          chartObject.xAxis =
            categories: getGroupByHeatMapCategoryNames(figure, 1)
        else
          chartObject.xAxis =
            categories: _getCategoriesFromFigure(figure)

        if getChartTypeFromFigure(figure) is 'heatmap' and getNumberOfGroupings(figure) is 2
          chartObject.yAxis =
            categories: getGroupByHeatMapCategoryNames(figure, 0)
        else if getChartTypeFromFigure(figure) is 'heatmap' and getNumberOfGroupings(figure) is 1
          # Heat Map based on Time Interval
          chartObject.yAxis = 
            categories: getTimeIntervalHeatMapCategoryNames(figure)
        else
          # Get y-axis(es)
          chartObject.yAxis = figure.presentation.highcharts.chart.yAxis

        # Set z-axis for Bubble chart
        if getChartTypeFromFigure(figure) in ['scatter', 'scattertrendline'] and getNumberOfMetrics(figure) is 3
          chartObject.zAxis = figure.presentation.highcharts.chart.zAxis
        # Insert series data
        # special series handling for pie or funnel charts
        if getChartTypeFromFigure(figure) in ['pie', 'funnel'] and isSingleSourceFigure(figure) 
          chartObject.series = _getPieOrFunnelSeriesDataFromFigure(figure)
        else if getChartTypeFromFigure(figure) in ['pie','funnel'] and !isSingleSourceFigure(figure)
          chartObject.series = _getFunnelPieMultipleMetricsSeriesDataFromFigure(figure)
        # special series handling for scatter charts
        else if getChartTypeFromFigure(figure) in ['scatter', 'scattertrendline']
          # _groupedByDataLabels(figure)
          chartObject.series =  _getScatterAndBubbleSeriesDataFromFigure(figure)
        # Special series handling for heat map charts
        else if getChartTypeFromFigure(figure) is 'heatmap'
          chartObject.series =  _getHeatMapSeriesDataFromFigure(figure)
        else
          chartObject.series = getSeriesDataFromFigure(figure)
      else if figure.data? and figure.reportQueryType is reportQueryType.SQL
        figure = _refreshSqlFigureCategoryValues(figure)
        chartObject = utils.copy(figure.presentation.highcharts)
        chartObject.series = _convertSqlFigureDataToHighchartSeries(figure)
        chartObject.tooltip =
          shared: false
          followPointer: true

        for series in chartObject.series
          series.marker = chartFormatterService.setMarkers(series, chartObject.chart.type)
      else
        chartObject = {}
      return chartObject

    _getGroupByRegex = () ->
      return /^\[GROUPBY(GA)?FILTER.*\]$/

    mapOverFromSafe = (query, f) ->
      from = angular.copy query.from
      unless Array.isArray(from)
        from = [from]

      from.map(f)

    # Is this filter alias using the reserved GROUPBYFILTER convetion?
    _isGroupByAlias = (alias) ->
      if alias instanceof Array
        return false
      else
       return alias.match(/\s*[AND]*\s*\[GROUPBY(GA)?FILTER.*\]/)?

    # Recursive function to remove any group by filters from the expressions
    _removeGroupByFromExpression = (expression) ->

      # We need to go deeper
      if expression instanceof Array
        expression = [
          _removeGroupByFromExpression(expression[0]),
          _removeGroupByFromExpression(expression[1]),
          _removeGroupByFromExpression(expression[2])
        ]

        if _isGroupByAlias(expression[1]) and _isGroupByAlias(expression[2])
          expression = ''
        else if _isGroupByAlias(expression[1])
          expression = expression[2]
        else if _isGroupByAlias(expression[2])
          expression = expression[1]

        # Catch the case where we now have a hole in our expression
        if expression[2] is ""
          expression = expression[1]

      return expression

    _removeGroupByValueFiltersFromWhere = (from) ->
      
      if from.where?
        for key, value of from.where
          if key.match(_getGroupByRegex())?
            delete from.where["#{key}"]

        from.where.expression = _removeGroupByFromExpression(from.where.expression)

        # Catch singular case
        if _isGroupByAlias(from.where.expression)
          from.where.expression = ''

      return from

    _isGaDimension = (column_id) ->
      is_string = isNaN(parseInt(column_id))
      return (("#{column_id}".match(/^ga:.*$/)?) || (is_string && !Object.values(rjqlStringConstants).includes(column_id)))

    _getOperatorId = (sid, operator) ->
      isGA4Live = if view?.metric?.isGA4Live? then true else false
      if _isGaDimension(sid)
        return operatorEnums.GA_COMPARISON_OPERATOR_IDS[operator]
      else if isGA4Live
        return operatorEnums.GA4_COMPARISON_OPERATOR_IDS[operator]
      else
        return operatorEnums.COMPARISON_OPERATOR_IDS[operator]

    # Get a filter from an RJQL where statement
    _getFilterFromWhere = (where) ->

      blankFilter =
        restrictions: []
        sequence:
          string: null

      for key, value of where
        if key is 'expression'
          blankFilter.sequence.string = value
        else
          restriction =
            left:
              column_id: value.field
            op: _getOperatorId(value.field, value.operator)
            right: value.value
          blankFilter.restrictions.push restriction

      return blankFilter

    # Returns a set of filters from a figure
    # Optionally include groupby filters
    getFiltersFromFigure = (figure, includeGroupByFilters = false) ->
      query = angular.copy(figure.query)

      if not includeGroupByFilters
        query.from = mapOverFromSafe(query, (f) =>
          _removeGroupByValueFiltersFromWhere(f)
        )

      mapOverFromSafe(query, (f) =>
        _getFilterFromWhere(f.where)
      )

    _getGroupByValueFilterFromWhere = (where) ->
      groupByValueFilters = []
      for key, value of where
        if key.match(_getGroupByRegex())?
          groupByValueFilters.push value

      return groupByValueFilters

    # Returns a set of filters from a figure
    getGroupByFiltersFromFigure = (figure) ->
      mapOverFromSafe(figure.query, (f) =>
        _getGroupByValueFilterFromWhere(f.where)
      )

    # Returns time column id if dimension is time based cohort dimension, else return dimension
    parseDimension = (dimension) ->
      return if _.isObject(dimension) then dimension.time else dimension

    # Returns dimensions from a figure object
    getDimensionsFromFigure = (figure) ->
      dimensions = []
      if figure.query.select.rows?
        for dimension in figure.query.select.rows
          if isSingleSourceFigure(figure)
            dimensions[0] ?= []
            dimensions[0].push parseDimension(dimension)
          else
            for from, index in figure.query.from
              dimensions[index] ?= []
              dimensions[index].push figure.query.from[index].bind[dimension]

      return dimensions

    isCohortFigure = (figure) ->
      columns = figure?.query?.select?.columns
      columns? and columns[0]?.function is "perioddiff"

    getCountingMembersFromFigure = (figure) ->
      measures = getMeasuresFromFigure(figure)
      measures[0]?['counting-members']

    isTimeBasedCohortFigure = (figure) ->
      if isCohortFigure(figure)
        rows = figure?.query?.select?.rows
        return rows? and _.isObject(rows[0]) and _.has(rows[0], 'time')
      else
        return false

    getWindowLengthByMetricAlias = (alias, figure) ->
      for measure in getMeasuresFromFigure(figure)
        if measure.type is 'function' and
          (measure.field[0] is alias or measure.label is alias)
            return if measure.offset?.value? then -measure.offset.value or
              figureEnums.PERSPECTIVE_CONSTANTS.DEFAULT_AVERAGE_WINDOW_LENGTH

      return figureEnums.PERSPECTIVE_CONSTANTS.DEFAULT_AVERAGE_WINDOW_LENGTH

    # Returns the time period from a figure object
    getTimeRangeFromFigure = (figure, version=0.1) ->
      if figure.query.timerange?
        timeRange = figure.query.timerange
        if !isCohortFigure(figure)
          timeRange.field = dateService.timeVersion(version)
      else
        timeRange =
          type: timeRangeEnums.MOVING_TIME_RANGE_TYPES.ALL_TIME
          start: timeRangeEnums.TIME_RANGE_VALUES.START
          end: timeRangeEnums.TIME_RANGE_VALUES.END
          field: dateService.timeVersion(version)

      return timeRange

    _parseExpression = (alias, expression) ->
      if expression instanceof Array
        # We need to go deeper
        dataPerspectives = _parseExpression(alias, expression[0])
        dataPerspectives = dataPerspectives.concat _parseExpression(alias, expression[1])
        dataPerspectives = dataPerspectives.concat _parseExpression(alias, expression[2])
        return dataPerspectives
      else if typeof expression is 'object' and expression.type is 'function' and expression.field[0] is alias
        # We've reached a relevent measure
        return [expression]
      else
        # return an empty array for anything else we find
        return []

    # Parses the figure to find out which data perspective this series should have
    # This gets complicated because any expression could use this metric and we need
    # to check all of them.
    getDataPerspectiveByMetricAlias = (alias, figure) ->
      dataPerspectives = []
      for measure in getMeasuresFromFigure(figure)
        switch measure.type
          when 'function'
            if isSingleSourceFigure(figure)
              dataPerspectives.push measure
            else if measure.field[0] is alias
              dataPerspectives.push measure
          when 'formula'
            dataPerspectives = dataPerspectives.concat _parseExpression(alias, measure.expression)
      return dataPerspectives

    containsGaMetric = (figure) ->
      if figure.query.from.metric instanceof Array
        return true

      for metric in figure.query.from
        if metric.metric instanceof Array
          return true

      return false

    getMetricIdsFromFigure = (figure) ->
      mapOverFromSafe(figure.query, (f) ->
        f.metric
      )

    sortDataIfApplicable = (figure) ->
      # We want to sort if this chart has a single source (or single formula),
      # a single dimension, and a time interval.
      if getTimeInterval(figure) isnt TIME_INTERVAL_VALUES.NONE and
        (isSingleSourceFigure(figure) or _hasSingleFormulaMeasure(figure)) and
        getDimensionsFromFigure(figure)[0]?.length is 1
          figure.data.rows = chartFormatterService.sortSeriesByName(figure.data.rows)
      # Sort by row name if there's no time interval and a single group-by dimension for rjql 0.1 only
      else if app.getRjqlVersion() is 0.1 and
        getTimeInterval(figure) is TIME_INTERVAL_VALUES.NONE and
        not isSingleSourceFigure(figure) and
        getDimensionsFromFigure(figure)[0]?.length is 1
          figure.data.rows = chartFormatterService.sortSeriesByName(figure.data.rows)

      return figure

    shouldConvertToScalar = (figure, allowed) ->
      if not allowed
        return false

      type = figure.presentationType.id
      TABLE = figureEnums.PRESENTATION_TYPES.TABLE
      SCALAR = figureEnums.PRESENTATION_TYPES.SCALAR

      if not figure.data? and type is TABLE
        return false
      else if not figure.data? and type is SCALAR
        return true

      return (type is TABLE or type is SCALAR) and figure.data.body.length is 1 and figure.data.body[0].length is 1

    identifyDataPerspective = (measure, version=0.1) ->
      # figure.query.select.measures -> measure
      switch version
        when 0.1 then _identifyDataPerspective_0_1(measure)
        when 1.0 then _identifyDataPerspective_1_0(measure)

    # We don't have data perspective types in RJQL, but we name them for convenience
    # in the report builder
    # This method identifies a data perspective and returns its type
    _identifyDataPerspective_0_1 = (measure) ->
      # Switching by perspective function
      switch measure?.function
        when 'sum'
          if measure.offset is ':time-range-start'
            dataPerspectiveType = 'cumulative'

        when 'diff' # Amount change perspectives
          if measure.offset.type is 'point-relative' and measure.offset.value is -1 and measure.offset.unit is 'year'
            dataPerspectiveType = 'amountVsPreviousYear'
          else
            dataPerspectiveType = 'amountVsPreviousPeriod'

        when 'diffpercent' # % change perspectives
          if measure.offset is ':time-range-start'
            dataPerspectiveType = 'percentOfFirstValueShowFirstValue'
          else if measure.offset is ':time-range-start-hide-first'
            dataPerspectiveType = 'percentOfFirstValueHideFirstValue'
          else if measure.offset.type is 'point-relative' and measure.offset.unit is 'year'
            dataPerspectiveType = 'percentVsPreviousYear'
          else
            dataPerspectiveType = 'percentVsPreviousPeriod'
        else
          dataPerspectiveType = 'standard'

      return dataPerspectiveType

    _identifyDataPerspective_1_0 = (measure) ->
      # Switching by perspective function
      switch measure?.function
        when 'rolling-aggregate'
          switch measure.op
            when 'sum'
              if measure.by is rjqlStringConstants.RJQL_PERIODDIFF
                dataPerspectiveType = 'cumulativeCohort'
              else
                dataPerspectiveType = 'cumulative'
            when 'cumulative-average-cohort'
              dataPerspectiveType = 'cumulativeAverageValuePerCohortMember'
            when 'average-cohort'
              dataPerspectiveType = 'averageValuePerCohortMember'
            when 'average'
              dataPerspectiveType = 'rollingAverage'

        when 'offset-diff' # Amount change perspectives
          if measure.offset.relative_to in [':point', 'point'] and measure.offset.value is -1 and measure.offset.unit is 'Year'
            dataPerspectiveType = 'amountVsPreviousYear'
          else
            dataPerspectiveType = 'amountVsPreviousPeriod'

        when 'offset-percent' # Percent of first value
          if measure.by is rjqlStringConstants.RJQL_PERIODDIFF and measure.offset.value is -1
            dataPerspectiveType = 'percentOfFirstValueHideFirstValueCohort'
          else if measure.by is rjqlStringConstants.RJQL_PERIODDIFF
            dataPerspectiveType = 'percentOfFirstValueShowFirstValueCohort'
          else
            dataPerspectiveType = 'percentOfFirstValueShowFirstValue'

        when 'offset-diff-percent' # % change perspectives
          if measure.offset.relative_to in [':point', 'point'] and measure.offset.unit is 'Year'
            dataPerspectiveType = 'percentVsPreviousYear'
          else
            dataPerspectiveType = 'percentVsPreviousPeriod'
        else
          dataPerspectiveType = 'standard'

      return dataPerspectiveType

    _divideFigure = (figure) ->
      dividedFigures = []
      rowsCopy = angular.copy(figure.data.rows)

      # Skip first value (cohort date) from the array of names and leave all others - grouping values
      groupingValues = rowsCopy.map( (row) -> _.rest(row.name) )
      uniqueGroupingValues = _.uniq(
        groupingValues
        true
        # Since "_.uniq" function can compare non-objects we convert the array to joined string
        (value) ->
          value.join('-')
      )

      # Divide the figure to list of figures for each set of grouping values
      for values in uniqueGroupingValues
        copyFigure = angular.copy(figure)
        copyFigure.data.rows = copyFigure.data.rows.filter(
          (row) ->
            _.every(
              values
              (value) ->
                value in row.name
            )
        )

        # Since the first column in the future table is date column then leave only dates in row names
        for row in copyFigure.data.rows
          row.name = row.name[0]
        # Name of the first column in the table - all grouping values.
        copyFigure.data.headers.rows = [values.map(correctNullOrBlankValue).join(', ')]
        # For correct determining of columnDefs property of DataTables options
        # object we need leave here only time cohort dimension
        copyFigure.query.select.rows = [copyFigure.query.select.rows[0]]
        dividedFigures.push(copyFigure)

      return dividedFigures

    _fillEmptyGroupBy = (figure) ->
      copyFigure = angular.copy(figure)

      # Name of the first column in the table.
      # Empty since no grouping values in response from the backend.
      copyFigure.data.headers.rows = [""]

      # For correct determining of columnDefs property of DataTables options
      # object we need leave here only time cohort dimension
      copyFigure.query.select.rows = [copyFigure.query.select.rows[0]]

      return [copyFigure]

    # If the figure is time-based cohort with grouping then divides this figure
    # to list of figures for each value in the grouping
    # (using in renderTable directive only !!!)
    splitFigureIfNecessary = (figure) ->
      if isTimeBasedCohortFigure(figure) and
        figure.query.select.rows.length > 1
          # If grouping values present in figure
          if Array.isArray(figure.data.rows[0].name)
            return _divideFigure(figure)
          # If grouping values don't present in figure (for example no data in timerange)
          else
            return _fillEmptyGroupBy(figure)
      else
        return [angular.copy(figure)]


    {
      #sql builder only
      addSeries
      removeSeries
      setSeries
      addCategory
      removeCategory
      setCategories
      addLabel
      removeLabel

      figurePresentationIsStacked
      doesFigureHaveMultiYAxes
      doesFigureHaveEnabledLegend
      genAxisOptions
      formatAxes
      getChartTypeFromFigure
      getMeasuresFromFigure
      setChartType
      isSingleSourceFigure
      getChartFromFigure
      getTimeInterval
      isGroupedByDimension
      getSeriesDataFromFigure
      mapOverFromSafe
      getFiltersFromFigure
      getDimensionsFromFigure
      getTimeRangeFromFigure
      getDataPerspectiveByMetricAlias
      containsGaMetric
      getMetricIdsFromFigure
      getUnDimensionedMeasureNames
      sortDataIfApplicable
      getGroupByFiltersFromFigure
      shouldConvertToScalar
      getMeasureDataFromFigureData
      isCohortFigure
      isTimeBasedCohortFigure
      getWindowLengthByMetricAlias
      identifyDataPerspective
      getCountingMembersFromFigure
      splitFigureIfNecessary
      correctNullOrBlankValue
      getNumberOfGroupings
      getNumberOfMetrics
      isPieFunnelAllowed
      isScatterBubbleAllowed
      isHeatMapAllowed
      getMetricNameForToolTip
      getDateTimeDataForToolTip
      getGroupByHeatMapCategoryNames
      getTimeIntervalHeatMapCategoryNames

      # Called in unit tests, should be treated as private
      _convertSqlFigureDataToHighchartSeries
      _getAxesTitle
      _getScatterAndBubbleSeriesDataFromFigure
      _getScatterChartDataLabels
      _getScatterDataLabelsByTimeInterval
      _getScatterTrendLineFromSeriesData
      _getMultiSeriesDataForBubbleChart
      _bubbleChartMultiSeries
      _getBubbleChartDataLabels
      _getBubbleChartDataLabelsByTimeInterval
      _isSingleSeriesBubbleChart
      _isMultiSeriesBubbleChart
      _isGroupedByHeatMapType
      _isTimeIntervalHeatMapType
      _getHeatMapSeriesDataFromFigure
    }
  ]